import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  useTheme
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime,
  Send as SendIcon
} from '@mui/icons-material';
import emailjs from '@emailjs/browser';
import trainers from '../../data/trainers';

const ContactSection = () => {
  const theme = useTheme();

  const [formData, setFormData] = useState({
    name: '',
    healthGoals: '',
    trainerSelection: '',
    phoneNumber: '',
    bestTimeToCall: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: null, message: '' });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: null, message: '' });

    // EmailJS service configuration
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    console.log('EmailJS Config:', {
      serviceId,
      templateId,
      publicKey,
    });

    // Check if credentials are loaded
    if (!serviceId || !templateId || !publicKey) {
      setSubmitStatus({
        type: 'error',
        message: 'EmailJS configuration is missing. Please check your .env file.'
      });
      setIsLoading(false);
      return;
    }

    // Validate form data
    if (!formData.name.trim()) {
      setSubmitStatus({
        type: 'error',
        message: 'Please provide your name.'
      });
      setIsLoading(false);
      return;
    }

    if (!formData.phoneNumber.trim()) {
      setSubmitStatus({
        type: 'error',
        message: 'Please provide your phone number.'
      });
      setIsLoading(false);
      return;
    }

    // Prepare template parameters
    const templateParams = {
      from_name: formData.name.trim(),
      health_goals: formData.healthGoals,
      trainer_selection: formData.trainerSelection,
      phone_number: formData.phoneNumber.trim(),
      best_time_to_call: formData.bestTimeToCall,
    };

    // Send email using EmailJS
    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        console.log('Email sent successfully:', response);
        console.log('Template params sent:', templateParams);
        setSubmitStatus({ type: 'success', message: 'Thank you! Your message has been sent successfully.' });
        // Reset form
        setFormData({
          name: '',
          healthGoals: '',
          trainerSelection: '',
          phoneNumber: '',
          bestTimeToCall: ''
        });
      })
      .catch((error) => {
        console.error('Failed to send email:', error);
        console.error('Error details:', error.text || error.message || error);
        setSubmitStatus({
          type: 'error',
          message: `Failed to send email: ${error.text || 'Please check your configuration and try again.'}`
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const businessHours = [
    { day: 'Monday', hours: '5:00 AM - 9:00 PM' },
    { day: 'Tuesday', hours: '5:00 AM - 9:00 PM' },
    { day: 'Wednesday', hours: '5:00 AM - 9:00 PM' },
    { day: 'Thursday', hours: '5:00 AM - 9:00 PM' },
    { day: 'Friday', hours: '5:00 AM - 6:00 PM' },
    { day: 'Saturday', hours: '5:00 AM - 6:00 PM' },
    { day: 'Sunday', hours: '8:00 AM - 5:00 PM' }
  ];

  const trainerOptions = [
    { value: '', label: 'Select a trainer' },
    ...trainers.map((trainer, index) => ({
      value: `trainer${index + 1}`,
      label: trainer.name
    }))
  ];

  const timeOptions = [
    { value: '', label: 'Select a time' },
    { value: 'morning', label: 'Morning (6 AM - 12 PM)' },
    { value: 'afternoon', label: 'Afternoon (12 PM - 5 PM)' },
    { value: 'evening', label: 'Evening (5 PM - 9 PM)' }
  ];

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        minHeight: '100vh',
        py: 8
      }}
    >
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 600,
                  mb: 3,
                  maxWidth: '100%',
                  textAlign: 'center'
                }}
              >
                Contact <Box component="span" sx={{ color: theme.palette.primary.main }}>Us</Box><br />
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.125rem', maxWidth: 600, mx: 'auto' }}>
            We'd love to hear from you. Get in touch with us today!
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Contact Information */}
        <Card
          sx={{
            mb: 6,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 4,
            boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
            }
          }}
        >
              <Box sx={{ p: 0 }}>
                <Typography
                  variant="h3"
                  sx={{
                    color: theme.palette.grey[700],
                    mb: 4,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 3,
                    pt: 3
                  }}
                >
                  <LocationIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                  Visit Us & Call Us
                </Typography>
                <Box sx={{ display: 'flex', gap: 6, flexDirection: { xs: 'column', md: 'row' }, p: { xs: 3, md: 5 } }}>
                  {/* Left Section - Image and Business Hours */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 4 }}>
                      <Box
                        component="img"
                        src={require('../../assets/images/VL.png')}
                        alt="Gym Exterior"
                        sx={{
                          width: '100%',
                          height: '250px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="h5" sx={{ color: theme.palette.grey[700], mb: 2, fontWeight: 600 }}>
                        Business Hours
                      </Typography>
                      <TableContainer component={Paper} sx={{ boxShadow: 'none', backgroundColor: 'transparent' }}>
                        <Table size="small">
                          <TableBody>
                            {businessHours.map((row, index) => (
                              <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  sx={{ border: 0, p: 0.5, color: theme.palette.grey[600] }}
                                >
                                  {row.day}
                                </TableCell>
                                <TableCell sx={{ border: 0, p: 0.5, color: theme.palette.grey[700], fontWeight: 500 }}>
                                  {row.hours}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>

                  {/* Right Section - Map, Address, and Phone Numbers */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 3 }}>
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3316.8!2d-118.1283!3d33.7845!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dd31e1c47ea28b%3A0x7e7f8e5e5e5e5e5e!2s3430%20Los%20Coyotes%20Diagonal%2C%20Long%20Beach%2C%20CA%2090808!5e0!3m2!1sen!2sus!4v1635959385185!5m2!1sen!2sus"
                        width="100%"
                        height="250px"
                        style={{
                          border: 0,
                          borderRadius: '12px',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                        }}
                        allowFullScreen=""
                        loading="lazy"
                        title="Gym Location"
                      ></iframe>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h5" sx={{ color: theme.palette.grey[700], mb: 2, fontWeight: 600 }}>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ color: theme.palette.grey[600] }}>
                        3430 Los Coyotes Diagonal
                        <br />
                        Long Beach, CA 90808
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="h5" sx={{ color: theme.palette.grey[700], mb: 2, fontWeight: 600 }}>
                      Contact 
                      </Typography>
                      <Typography variant="body1" sx={{ color: theme.palette.grey[600], mb: 2 }}>
                        <PhoneIcon sx={{ fontSize: 20, mr: 1, color: theme.palette.primary.main }} />
                        <strong> Phone:</strong> (562) 714-1188
                      </Typography>
                      <Typography variant="body1" sx={{ color: theme.palette.grey[600] }}>
                        <strong>  <EmailIcon sx={{ color: 'primary.main', fontSize: 20, mr: 1, }} /> Email:</strong> Varsityfitnesslb@outlook.com
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Card>

        {/* Email Us Form Section */}
        <Card
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 4,
            boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
            overflow: 'visible'
          }}
        >
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                color: theme.palette.grey[700],
                mb: 4,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <EmailIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              Email Us
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              {/* Name */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                      }
                    }
                  }}
                />
              </Box>

              {/* Physical Health Goals */}
              <Box sx={{ mb: 3 }}>
                <FormControl
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                      }
                    }
                  }}
                >
                  <InputLabel>Physical Health Goals</InputLabel>
                  <Select
                    name="healthGoals"
                    value={formData.healthGoals}
                    onChange={handleInputChange}
                    label="Physical Health Goals"
                  >
                    <MenuItem value="">Select a goal</MenuItem>
                    <MenuItem value="weight-loss">Weight Loss</MenuItem>
                    <MenuItem value="muscle-gain">Muscle Gain</MenuItem>
                    <MenuItem value="strength">Build Strength</MenuItem>
                    <MenuItem value="endurance">Improve Endurance</MenuItem>
                    <MenuItem value="flexibility">Flexibility & Mobility</MenuItem>
                    <MenuItem value="overall">Overall Health & Fitness</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Personal Trainer Selection */}
              <Box sx={{ mb: 3 }}>
                <FormControl
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                      }
                    }
                  }}
                >
                  <InputLabel>Select a Trainer</InputLabel>
                  <Select
                    name="trainerSelection"
                    value={formData.trainerSelection}
                    onChange={handleInputChange}
                    label="Select a Trainer"
                  >
                    {trainerOptions.map((trainer) => (
                      <MenuItem key={trainer.value} value={trainer.value}>
                        {trainer.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Phone Number */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                      }
                    }
                  }}
                />
              </Box>

              {/* Best Time to Call */}
              <Box sx={{ mb: 3 }}>
                <FormControl
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px rgba(178, 35, 53, 0.2)`
                      }
                    }
                  }}
                >
                  <InputLabel>Best Time to Call</InputLabel>
                  <Select
                    name="bestTimeToCall"
                    value={formData.bestTimeToCall}
                    onChange={handleInputChange}
                    label="Best Time to Call"
                  >
                    {timeOptions.map((time) => (
                      <MenuItem key={time.value} value={time.value}>
                        {time.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                endIcon={<SendIcon />}
                disabled={isLoading}
                sx={{
                  borderRadius: 3,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: '0 10px 25px rgba(178, 35, 53, 0.3)',
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    background: `linear-gradient(135deg, rgba(178, 35, 53, 0.5) 0%, rgba(216, 85, 102, 0.5) 100%)`,
                    cursor: 'not-allowed',
                    boxShadow: 'none',
                    transform: 'none'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {isLoading ? 'Sending...' : 'Send Message'}
              </Button>

              {/* Status Message */}
              {submitStatus.type && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography
                    sx={{
                      color: submitStatus.type === 'success' ? 'green' : 'red',
                      fontWeight: 500,
                      fontSize: '0.9rem'
                    }}
                  >
                    {submitStatus.message}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default ContactSection;
